import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FileUploadComponent from "./FileUploadComponent";
import CommonAPIService from '../services/commonAPIService';
import { useNavigate } from 'react-router-dom';


const TerminateApplicationDialog = ({appId,stage}) => {
  const navigate = useNavigate();
  const [remarks, setRemarks] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [terminateLoading, setTerminateLoading] = useState(false);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!remarks || remarks.trim() === "") {
      newErrors.remarks = "Enter Remarks";
    }

    if (!password || password.trim() === "") {
      newErrors.password = "Enter password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
      try {
        if (validateForm()) {
        setTerminateLoading(true);
        const passwordVerification = await CommonAPIService.verifyPassword(
          localStorage.getItem("orgId"),
          password.trim()
        );
  
        if (!passwordVerification.success) {
          setNotification({
            open: true,
            message: 'Invalid password. Please try again.',
            severity: 'error'
          });
          return;
        }
  
        // Call terminate application API using the service
        const result = await CommonAPIService.terminateApplication(
          appId,
          localStorage.getItem("orgId"),
          remarks.trim(),
          uploadedFile ? uploadedFile.fileName : null,
        );
  
        if (result.success) {
          // Show success notification
          setNotification({
            open: true,
            message: 'Application terminated successfully. Redirecting...',
            severity: 'success'
          });
  
          // Close dialog
          setTerminateDialogOpen(false);
  
          // Redirect to dashboard after successful termination
          setTimeout(() => {
            localStorage.clear();
            localStorage.setItem("logout", Date.now());
            navigate('/main/login')
          }, 2000);
        } else {
          throw new Error(result.message || 'Failed to terminate application');
        }
       }
      } catch (error) {
        console.error('Error terminating application:', error);
        setNotification({
          open: true,
          message: error.message || 'Failed to terminate application. Please try again.',
          severity: 'error'
        });
      } finally {
        setTerminateLoading(false);
      }
    };

    // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ open: false, message: '', severity: 'success' });
  };

  // Handle dialog close
  const handleClose = () => {
    if (!terminateLoading) {
      // Reset form
      setRemarks("");
      setUploadedFile(null);
      setPassword("");
      setErrors({});
      handleTerminateDialogClose();
    }
  };

  // Handle file upload
  const handleFileUpload = (fieldName, uploadData) => {
    setUploadedFile(uploadData);
  };

  const handleTerminateDialogOpen = () => {
    setTerminateDialogOpen(true);
  };

  const handleTerminateDialogClose = () => {
    setTerminateDialogOpen(false);
  };

  return (
    <>
      {!stage && (
        <Button
          variant="contained"
          startIcon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
          onClick={handleTerminateDialogOpen}
          size="small"
          sx={{
            background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
            color: "#333",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(255, 154, 158, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #e8898c 0%, #e5b6d8 100%)",
              boxShadow: "0 4px 12px rgba(255, 154, 158, 0.4)",
            },
          }}
        >
          Terminate this Application
        </Button>
        // <Button
        //   variant="contained"
        //   color="error"
        //   onClick={handleTerminateDialogOpen}
        //   sx={{
        //     fontWeight: "bold",
        //     position: "fixed",
        //     bottom: '2%',
        //     right: '3.5%',
        //     zIndex: 1000,
        //     fontSize: "13px",
        //     boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
        //     "&:hover": {
        //       boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
        //       transform: "translateY(-2px)",
        //       transition: "all 0.2s ease-in-out",
        //     },
        //   }}
        // >
        //   <WarningAmberIcon sx={{ fontSize: 15, mr: 0.5 }} />
        //   Terminate this Application
        // </Button>
      )}
      <Dialog
        open={terminateDialogOpen}
        onClose={handleTerminateDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f44336",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
          }}
        >
          <WarningAmberIcon />
          Terminate this application
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 3, fontSize: "13px" }}>
            Use this Option to close this application. Once submitted this
            application will be permanently closed and no further action can be
            taken on it.
          </Alert>

          {/* Remarks Field */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Enter Remarks <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (errors.remarks) {
                  setErrors({ ...errors, remarks: "" });
                }
              }}
              error={!!errors.remarks}
              helperText={errors.remarks}
              placeholder="Enter your remarks here..."
              disabled={terminateLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            />
          </Box>

          {/* File Upload */}
          <Box sx={{ mb: 3 }}>
            <FileUploadComponent
              fieldName="terminateAppFile"
              label="Upload File (optional)"
              accept=".pdf,.jpg,.jpeg,.png,"
              maxSize={10}
              onChange={handleFileUpload}
              initialFile={uploadedFile}
            />
          </Box>

          {/* Password Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Enter Password <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: "" });
                }
              }}
              error={!!errors.password}
              helperText={errors.password}
              placeholder="Enter your password"
              disabled={terminateLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleTerminateDialogClose}
            variant="outlined"
            color="error"
            disabled={terminateLoading}
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={terminateLoading}
            sx={{ minWidth: 100 }}
          >
            {terminateLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TerminateApplicationDialog;
